import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MORNING_PICK_LIMIT, plural, shouldSuggestSplit } from '../utils/focus';

// Общая шторка снизу. Тап по затемнению закрывает её.
export function BottomSheet({ open, onClose, title, children, labelId }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="sheet-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelId}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="sheet-grip" aria-hidden="true" />
            {title && <h3 className="sheet-title" id={labelId}>{title}</h3>}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// «Позже»: сегодня / завтра / через неделю. На третьем разе — предложение разбить.
export function PostponeSheet({ task, onClose, onPostpone, onSplit }) {
  const [step, setStep] = useState('');
  useEffect(() => { setStep(''); }, [task]);
  const suggestSplit = shouldSuggestSplit(task);
  const times = (task?.postpone_count || 0) + 1;

  return (
    <BottomSheet open={!!task} onClose={onClose} title="Когда вернуться к задаче?" labelId="postpone-title">
      {task && (
        <>
          <p className="sheet-subject">{task.description}</p>
          {suggestSplit && (
            <div className="sheet-split">
              <p>
                Откладываете уже {times}-й раз. Может, задача слишком большая? Запишите первый
                маленький шаг — он станет главным, а сама задача уедет на завтра.
              </p>
              <form
                className="sheet-split-form"
                onSubmit={e => {
                  e.preventDefault();
                  if (step.trim()) onSplit(task, step.trim());
                }}
              >
                <input
                  id="split-step"
                  className="sheet-input"
                  placeholder="Например: открыть документ и набросать план"
                  value={step}
                  onChange={e => setStep(e.target.value)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <button type="submit" className="focus-btn focus-btn-primary" disabled={!step.trim()}>
                  Сделать первым шагом
                </button>
              </form>
            </div>
          )}
          <div className="sheet-options">
            <button type="button" className="sheet-option" onClick={() => onPostpone(task, 'later')}>
              <b>Позже сегодня</b><span>уберу из «Сейчас», вернусь вечером</span>
            </button>
            <button type="button" className="sheet-option" onClick={() => onPostpone(task, 'tomorrow')}>
              <b>Завтра</b><span>перенести на завтра</span>
            </button>
            <button type="button" className="sheet-option" onClick={() => onPostpone(task, 'week')}>
              <b>Через неделю</b><span>не горит, вспомню позже</span>
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// Утренний выбор: до трёх главных задач на день.
export function MorningPickSheet({ open, tasks, initial, onClose, onSave }) {
  const [selected, setSelected] = useState([]);
  useEffect(() => { if (open) setSelected(initial || []); }, [open, initial]);

  const toggle = id => setSelected(prev => {
    if (prev.includes(id)) return prev.filter(x => x !== id);
    if (prev.length >= MORNING_PICK_LIMIT) return prev;
    return [...prev, id];
  });

  return (
    <BottomSheet open={open} onClose={onClose} title="Что сегодня главное?" labelId="pick-title">
      <p className="sheet-hint">Выберите до трёх задач — они будут первыми в «Сейчас».</p>
      <ul className="sheet-pick-list">
        {(tasks || []).map(t => {
          const on = selected.includes(t.id);
          const disabled = !on && selected.length >= MORNING_PICK_LIMIT;
          return (
            <li key={t.id}>
              <button
                type="button"
                className={`sheet-pick ${on ? 'is-on' : ''}`}
                onClick={() => toggle(t.id)}
                disabled={disabled}
                aria-pressed={on}
              >
                <span className="sheet-pick-mark">{on ? selected.indexOf(t.id) + 1 : ''}</span>
                <span className="sheet-pick-text">{t.description}</span>
                {t.important && <span className="sheet-pick-star" aria-label="важная">★</span>}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="sheet-footer">
        <button type="button" className="focus-btn focus-btn-secondary" onClick={onClose}>Не сейчас</button>
        <button type="button" className="focus-btn focus-btn-primary" onClick={() => onSave(selected)} disabled={!selected.length}>
          Начать день
        </button>
      </div>
    </BottomSheet>
  );
}

// Итог дня: что сделано и что осталось; остальное — на завтра одним тапом.
export function DayCloseSheet({ open, doneCount, left, onClose, onMoveAll }) {
  const total = doneCount + left.length;
  return (
    <BottomSheet open={open} onClose={onClose} title="Итог дня" labelId="dayclose-title">
      <div className="sheet-score">
        <b>{doneCount}</b>
        <span>из {total} {plural(total, 'задачи', 'задач', 'задач')} сделано</span>
      </div>
      {left.length > 0 ? (
        <>
          <p className="sheet-hint">Осталось {left.length} — можно спокойно перенести на завтра.</p>
          <ul className="sheet-left-list">
            {left.slice(0, 5).map(t => <li key={t.id}>{t.description}</li>)}
            {left.length > 5 && <li className="sheet-more">и ещё {left.length - 5}</li>}
          </ul>
          <div className="sheet-footer">
            <button type="button" className="focus-btn focus-btn-secondary" onClick={onClose}>Оставить</button>
            <button type="button" className="focus-btn focus-btn-primary" onClick={onMoveAll}>Всё на завтра</button>
          </div>
        </>
      ) : (
        <>
          <p className="sheet-hint">Всё сделано. День закрыт.</p>
          <div className="sheet-footer">
            <button type="button" className="focus-btn focus-btn-primary" onClick={onClose}>Отлично</button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// Новая задача из кнопки «+» в доке.
// Срок новой задачи: кнопки вместо «назови дату словами» — без ИИ и без лишних касаний.
// По умолчанию — по вкладке. Если срок назван в тексте («в пятницу»), он главнее кнопки.
const ADD_DUE_OPTIONS = [
  { key: 'today', label: 'Сегодня' },
  { key: 'tomorrow', label: 'Завтра' },
  { key: 'none', label: 'Без даты' },
];

export function defaultAddDue(tab) {
  return tab === 'today' ? 'today' : tab === 'plans' ? 'tomorrow' : 'none';
}

export function AddTaskSheet({ open, value, onChange, onClose, onSubmit, tab }) {
  const [due, setDue] = useState(defaultAddDue(tab));
  const [picked, setPicked] = useState('');
  useEffect(() => {
    if (open) { setDue(defaultAddDue(tab)); setPicked(''); }
  }, [open, tab]);
  const choose = (key) => { setDue(key); if (key !== 'pick') setPicked(''); };
  return (
    <BottomSheet open={open} onClose={onClose} title="Новая задача" labelId="add-title">
      <form className="sheet-add" onSubmit={e => onSubmit(e, due === 'pick' ? picked : due)}>
        <input
          id="add-task-input"
          className="sheet-input"
          placeholder="Например: позвонить в банк в пятницу"
          value={value}
          onChange={e => onChange(e.target.value)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <div className="add-due" role="radiogroup" aria-label="Срок">
          {ADD_DUE_OPTIONS.map(o => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={due === o.key}
              className={`add-due-chip ${due === o.key ? 'is-on' : ''}`}
              onClick={() => choose(o.key)}
            >
              {o.label}
            </button>
          ))}
          <label className={`add-due-chip add-due-pick ${due === 'pick' ? 'is-on' : ''}`}>
            {due === 'pick' && picked ? picked.split('-').reverse().slice(0, 2).join('.') : 'Дата…'}
            <input
              type="date"
              aria-label="Выбрать дату"
              value={picked}
              onChange={e => { setPicked(e.target.value); setDue(e.target.value ? 'pick' : defaultAddDue(tab)); }}
            />
          </label>
        </div>
        <p className="sheet-hint">Срок можно назвать и словами: «в пятницу», «8 октября», «через неделю».</p>
        <button type="submit" className="focus-btn focus-btn-primary" disabled={!value.trim()}>Добавить</button>
      </form>
    </BottomSheet>
  );
}

// Разбор завалов: просроченное по одной карточке, у каждой пять исходов.
// Очередь фиксируется при открытии, чтобы задачи не прыгали под пальцем.
const TRIAGE_ACTIONS = [
  { kind: 'done', label: 'Сделано', primary: true },
  { kind: 'today', label: 'Сегодня' },
  { kind: 'tomorrow', label: 'Завтра' },
  { kind: 'idea', label: 'В идеи' },
  { kind: 'delete', label: 'Удалить', danger: true },
];

export function TriageSheet({ open, tasks, daysOverdue, onClose, onAction }) {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [stats, setStats] = useState({});
  useEffect(() => {
    if (open) { setQueue(tasks || []); setIndex(0); setStats({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const current = queue[index];
  const act = (kind) => {
    onAction(current, kind);
    setStats(prev => ({ ...prev, [kind]: (prev[kind] || 0) + 1 }));
    setIndex(i => i + 1);
  };
  const summary = [
    stats.done && `сделано ${stats.done}`,
    stats.today && `на сегодня ${stats.today}`,
    stats.tomorrow && `на завтра ${stats.tomorrow}`,
    stats.idea && `в идеи ${stats.idea}`,
    stats.delete && `удалено ${stats.delete}`,
  ].filter(Boolean).join(', ');

  return (
    <BottomSheet open={open} onClose={onClose} title={current ? 'Разберём завалы' : 'Разобрано'} labelId="triage-title">
      {current ? (
        <>
          <p className="sheet-hint">
            {index + 1} из {queue.length}. Для каждой — честное решение, без чувства вины.
          </p>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={current.id}
              className="triage-card"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30, transition: { duration: 0.14 } }}
            >
              <span className="focus-tag is-late">
                просрочено {daysOverdue(current) === 1 ? 'со вчера' : `${daysOverdue(current)} ${plural(daysOverdue(current), 'день', 'дня', 'дней')}`}
              </span>
              <h4 className="triage-title">{current.description}</h4>
            </motion.div>
          </AnimatePresence>
          <div className="triage-actions">
            {TRIAGE_ACTIONS.map(a => (
              <button
                key={a.kind}
                type="button"
                className={`triage-btn ${a.primary ? 'is-primary' : ''} ${a.danger ? 'is-danger' : ''}`}
                onClick={() => act(a.kind)}
              >
                {a.label}
              </button>
            ))}
          </div>
          <button type="button" className="focus-link triage-later" onClick={onClose}>Хватит на сегодня</button>
        </>
      ) : (
        <>
          <p className="sheet-hint">{summary ? `Готово: ${summary}.` : 'Просроченного больше нет.'} Список снова ваш.</p>
          <div className="sheet-footer">
            <button type="button" className="focus-btn focus-btn-primary" onClick={onClose}>Отлично</button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// «Протухшие» задачи: просрочены дольше двух недель. Сама по себе такая задача
// уже не сдвинется, поэтому решений три: первый шаг (он идёт на сегодня, задача —
// на неделю), в идеи (без срока) или отпустить (засчитывается в итогах недели).
const STALE_ACTIONS = [
  { kind: 'step', label: 'Первый шаг', primary: true },
  { kind: 'idea', label: 'В идеи' },
  { kind: 'done', label: 'Уже сделал' },
  { kind: 'release', label: 'Отпустить', danger: true, wide: true },
];

export function StaleSheet({ open, tasks, daysOverdue, onClose, onAction }) {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [stats, setStats] = useState({});
  const [stepFor, setStepFor] = useState(null);
  const [step, setStep] = useState('');
  useEffect(() => {
    if (open) { setQueue(tasks || []); setIndex(0); setStats({}); setStepFor(null); setStep(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const current = queue[index];
  const next = (kind) => {
    setStats(prev => ({ ...prev, [kind]: (prev[kind] || 0) + 1 }));
    setStepFor(null);
    setStep('');
    setIndex(i => i + 1);
  };
  const act = (kind) => {
    if (kind === 'step') { setStepFor(current.id); return; }
    onAction(current, kind);
    next(kind);
  };
  const summary = [
    stats.step && `первых шагов ${stats.step}`,
    stats.idea && `в идеи ${stats.idea}`,
    stats.done && `сделано ${stats.done}`,
    stats.release && `отпущено ${stats.release}`,
  ].filter(Boolean).join(', ');
  const days = current ? daysOverdue(current) : 0;

  return (
    <BottomSheet open={open} onClose={onClose} title={current ? 'Висят дольше двух недель' : 'Разобрано'} labelId="stale-title">
      {current ? (
        <>
          <p className="sheet-hint">
            {index + 1} из {queue.length}. Почти всё, что делают, делают в первые дни. Такие задачи сами
            уже не сдвинутся — решим их судьбу.
          </p>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={current.id}
              className="triage-card"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30, transition: { duration: 0.14 } }}
            >
              <span className="focus-tag is-late">висит {days} {plural(days, 'день', 'дня', 'дней')}</span>
              <h4 className="triage-title">{current.description}</h4>
            </motion.div>
          </AnimatePresence>
          {stepFor === current.id ? (
            <form
              className="sheet-split-form"
              onSubmit={e => {
                e.preventDefault();
                if (!step.trim()) return;
                onAction(current, 'step', step.trim());
                next('step');
              }}
            >
              <input
                className="sheet-input"
                placeholder="Самое маленькое действие: позвонить, открыть, написать"
                value={step}
                onChange={e => setStep(e.target.value)}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <button type="submit" className="focus-btn focus-btn-primary" disabled={!step.trim()}>
                Шаг на сегодня
              </button>
              <button type="button" className="focus-link" onClick={() => setStepFor(null)}>Назад</button>
            </form>
          ) : (
            <div className="triage-actions">
              {STALE_ACTIONS.map(a => (
                <button
                  key={a.kind}
                  type="button"
                  className={`triage-btn ${a.primary ? 'is-primary' : ''} ${a.danger ? 'is-danger' : ''} ${a.wide ? 'is-wide' : ''}`}
                  onClick={() => act(a.kind)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="focus-link triage-later" onClick={onClose}>Хватит на сегодня</button>
        </>
      ) : (
        <>
          <p className="sheet-hint">
            {summary ? `Готово: ${summary}.` : 'Протухших задач больше нет.'} Отпустить — тоже решение: список стал честнее.
          </p>
          <div className="sheet-footer">
            <button type="button" className="focus-btn focus-btn-primary" onClick={onClose}>Отлично</button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

// Описание задачи: полный текст, из которого она сделана. Читать — со ссылками,
// править — по кнопке. Пустое описание при сохранении убирается.
export function DetailsSheet({ task, onClose, onSave, LinkifyText }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  useEffect(() => { setEditing(false); setValue(task?.details || ''); }, [task]);
  return (
    <BottomSheet open={!!task} onClose={onClose} title={task ? task.description : ''} labelId="details-title">
      {task && (editing ? (
        <form className="sheet-add" onSubmit={e => { e.preventDefault(); onSave(task, value); }}>
          <textarea
            id="details-input"
            className="sheet-input sheet-textarea"
            value={value}
            onChange={e => setValue(e.target.value)}
            rows={8}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <div className="sheet-footer">
            <button type="button" className="focus-btn focus-btn-secondary" onClick={() => setEditing(false)}>Отмена</button>
            <button type="submit" className="focus-btn focus-btn-primary">Сохранить</button>
          </div>
        </form>
      ) : (
        <>
          {task.details
            ? <LinkifyText text={task.details} className="sheet-details" />
            : <p className="sheet-hint">Описания нет. Сюда можно записать подробности.</p>}
          <div className="sheet-footer">
            <button type="button" className="focus-btn focus-btn-secondary" onClick={onClose}>Закрыть</button>
            <button type="button" className="focus-btn focus-btn-primary" onClick={() => setEditing(true)}>
              {task.details ? 'Изменить' : 'Добавить описание'}
            </button>
          </div>
        </>
      ))}
    </BottomSheet>
  );
}

// Разбор идей по Эйзенхауэру: идеи по одной (старые первыми), четыре квадранта.
// «Важно» не ставит дату — важная идея без срока и так лежит в квадранте «Важно».
const IDEA_SORT = [
  { kind: 'do', label: 'Важно и срочно', hint: '★ и на сегодня', icon: '🔥' },
  { kind: 'plan', label: 'Важно', hint: '★, без даты', icon: '📅' },
  { kind: 'quick', label: 'Срочно', hint: 'на сегодня', icon: '⚡' },
  { kind: 'later', label: 'Когда-нибудь', hint: 'оставить идеей', icon: '☰' },
];

export function IdeasSortSheet({ open, ideas, onClose, onSort, onUndo }) {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (open) { setQueue(ideas || []); setIndex(0); setHistory([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const current = queue[index];
  const act = (kind) => {
    onSort(current, kind);
    setHistory(h => [...h, { task: current, kind }]);
    setIndex(i => i + 1);
  };
  const skip = () => { setHistory(h => [...h, { task: current, kind: 'skip' }]); setIndex(i => i + 1); };
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    if (last.kind !== 'skip') onUndo(last.task, last.kind);
    setHistory(h => h.slice(0, -1));
    setIndex(i => Math.max(0, i - 1));
  };
  const counts = history.reduce((acc, h) => ({ ...acc, [h.kind]: (acc[h.kind] || 0) + 1 }), {});
  const summary = [
    counts.do && `важно и срочно ${counts.do}`,
    counts.plan && `важно ${counts.plan}`,
    counts.quick && `срочно ${counts.quick}`,
    counts.later && `когда-нибудь ${counts.later}`,
    counts.delete && `удалено ${counts.delete}`,
  ].filter(Boolean).join(', ');

  return (
    <BottomSheet open={open} onClose={onClose} title={current ? 'Разбор идей' : 'Идеи разобраны'} labelId="ideas-sort-title">
      {current ? (
        <>
          <div className="ideas-sort-top">
            <span className="sheet-hint">{index + 1} из {queue.length}</span>
            {history.length > 0 && <button type="button" className="focus-link" onClick={undo}>← Отменить</button>}
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div
              key={current.id}
              className="triage-card"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30, transition: { duration: 0.14 } }}
            >
              <h4 className="triage-title">{current.description}</h4>
              {current.details && <p className="ideas-sort-details">{current.details}</p>}
            </motion.div>
          </AnimatePresence>
          <div className="ideas-sort-grid">
            {IDEA_SORT.map(q => (
              <button key={q.kind} type="button" className={`ideas-sort-btn q-${q.kind}`} onClick={() => act(q.kind)}>
                <b><span aria-hidden="true">{q.icon}</span> {q.label}</b>
                <span>{q.hint}</span>
              </button>
            ))}
          </div>
          <div className="ideas-sort-bottom">
            <button type="button" className="focus-link ideas-sort-delete" onClick={() => act('delete')}>Удалить</button>
            <button type="button" className="focus-link" onClick={skip}>Пропустить</button>
          </div>
        </>
      ) : (
        <>
          <p className="sheet-hint">{summary ? `Разложено: ${summary}.` : 'Ничего не поменялось.'}</p>
          <div className="sheet-footer">
            {history.length > 0 && <button type="button" className="focus-btn focus-btn-secondary" onClick={undo}>← Отменить</button>}
            <button type="button" className="focus-btn focus-btn-primary" onClick={onClose}>Готово</button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}
